import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { FileText, Camera, CheckCircle, AlertTriangle } from "lucide-react";
import { compressImage } from "../../lib/imageUtils";
import { toast } from "sonner";

export const JobDetailWorkSection = ({
  job,
  isMechanic,
  canEdit,
  workNotes,
  setWorkNotes,
  mechanicSerialNumber,
  setMechanicSerialNumber,
  serialNumberSectionPhoto,
  setSerialNumberSectionPhoto,
  onUpdateJob,
  onAutoSaveSerialNumber,
}) => {
  const handleWorkNotesChange = (e) => {
    setWorkNotes(e.target.value);
  };

  const handleWorkNotesBlur = async () => {
    if (workNotes !== job.work_notes) {
      await onUpdateJob({ work_notes: workNotes });
    }
  };

  const handleSerialNumberChange = (e) => {
    const value = e.target.value;
    setMechanicSerialNumber(value);
    // Auto-save after a brief delay
    if (onAutoSaveSerialNumber) {
      onAutoSaveSerialNumber(value, serialNumberSectionPhoto || job?.serial_number_photo);
    }
  };

  const handleSerialNumberPhotoCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      setSerialNumberSectionPhoto(compressed);
      // Auto-save with new photo
      if (onAutoSaveSerialNumber) {
        onAutoSaveSerialNumber(mechanicSerialNumber || job?.mechanic_serial_number, compressed);
      }
      toast.success("Serial number photo captured");
    } catch (error) {
      toast.error("Failed to capture photo");
    }
  };

  const handleSaveSerialNumber = async () => {
    if (!mechanicSerialNumber?.trim()) {
      toast.error("Please enter the serial number");
      return;
    }
    if (!serialNumberSectionPhoto && !job?.serial_number_photo) {
      toast.error("Please capture a photo of the serial number plate");
      return;
    }

    const updateData = {
      mechanic_serial_number: mechanicSerialNumber.trim(),
    };
    if (serialNumberSectionPhoto) {
      updateData.serial_number_photo = serialNumberSectionPhoto;
    }

    await onUpdateJob(updateData);
    toast.success("Serial number saved");
  };

  return (
    <Card className="border-slate-200 shadow-sm rounded-sm flex-1">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Work Details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Job Description */}
        <div>
          <Label className="text-xs text-slate-500 uppercase font-semibold">Description</Label>
          <p className="text-slate-800 mt-1">{job.description || "No description provided"}</p>
        </div>

        {/* Work Notes */}
        {((!isMechanic) || (isMechanic && job.check_in)) && (
          <div>
            <Label className="text-xs text-slate-500 uppercase font-semibold">Work Notes</Label>
            {canEdit ? (
              <Textarea
                value={workNotes}
                onChange={handleWorkNotesChange}
                onBlur={handleWorkNotesBlur}
                placeholder="Enter work notes..."
                className="mt-1 rounded-sm"
                rows={3}
                data-testid="work-notes-input"
              />
            ) : (
              <p className="text-slate-800 mt-1 whitespace-pre-wrap">
                {job.work_notes || "No work notes"}
              </p>
            )}
          </div>
        )}

        {/* Serial Number Section - Only for mechanics after check-in */}
        {isMechanic && job.check_in && job.status !== "completed" && (
          <div className="border-t pt-4 mt-4">
            <Label className="text-xs text-slate-500 uppercase font-semibold flex items-center gap-2">
              Serial Number
              {job.mechanic_serial_number && job.serial_number_photo ? (
                <CheckCircle className="w-4 h-4 text-emerald-500" />
              ) : (
                <AlertTriangle className="w-4 h-4 text-amber-500" />
              )}
              <span className="text-red-500">*</span>
            </Label>
            <p className="text-xs text-slate-400 mb-2">
              Required before checkout - Enter the serial number and take a photo of the plate
            </p>

            {/* Serial Number Input */}
            <div className="space-y-3">
              <Input
                value={mechanicSerialNumber}
                onChange={handleSerialNumberChange}
                placeholder="Enter excavator serial number"
                className="rounded-sm"
                data-testid="serial-number-input"
              />

              {/* Serial Number Photo */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm"
                  onClick={() => document.getElementById("serial-photo-input").click()}
                  data-testid="capture-serial-photo-btn"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  {serialNumberSectionPhoto || job?.serial_number_photo ? "Retake Photo" : "Take Photo"}
                </Button>
                <input
                  id="serial-photo-input"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleSerialNumberPhotoCapture}
                />
                {(serialNumberSectionPhoto || job?.serial_number_photo) && (
                  <span className="text-xs text-emerald-600 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Photo captured
                  </span>
                )}
              </div>

              {/* Preview */}
              {(serialNumberSectionPhoto || job?.serial_number_photo) && (
                <div className="mt-2">
                  <img
                    src={serialNumberSectionPhoto || job?.serial_number_photo}
                    alt="Serial number"
                    className="w-32 h-32 object-cover rounded-sm border cursor-pointer hover:opacity-80"
                  />
                </div>
              )}

              <Button
                onClick={handleSaveSerialNumber}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 rounded-sm"
                data-testid="save-serial-btn"
              >
                Save Serial Number
              </Button>
            </div>
          </div>
        )}

        {/* Show saved serial number for non-mechanics or completed jobs */}
        {(!isMechanic || job.status === "completed") && job.mechanic_serial_number && (
          <div className="border-t pt-4 mt-4">
            <Label className="text-xs text-slate-500 uppercase font-semibold">Serial Number</Label>
            <p className="text-slate-800 mt-1 font-mono">{job.mechanic_serial_number}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default JobDetailWorkSection;
